/**
 * Case base type (spec 022 / #6) — the material the case is made of. It is BOTH a saved
 * template field and a compositing input: `baseTypeProfile` seeds the MaskEditor product
 * profile so the baked mockup reflects the material (glass → clear/glossy with the phone
 * composited behind the design; TPU → opaque). Pure + framework-free for unit testing.
 */
import type { ProductCategory } from "./compositing.js";

export interface BaseTypeOption {
  value: string;
  label: string;
}

/** Selectable base types (the wizard + editor dropdowns). Lowercase values persist. */
export const BASE_TYPES: BaseTypeOption[] = [
  { value: "tpu", label: "TPU (flexible)" },
  { value: "glass", label: "Glass" },
  { value: "clear", label: "Clear / transparent" },
  { value: "silicone", label: "Silicone" },
  { value: "hard", label: "Hard / polycarbonate" },
  { value: "leather", label: "Leather" },
  { value: "metal", label: "Metal" },
  { value: "wood", label: "Wood" },
];

const PROFILE_BY_TYPE: Record<string, { category: ProductCategory; clear: boolean }> = {
  // Glass/clear backs: composite the phone behind the design (glossy, see-through).
  glass: { category: "phone_case", clear: true },
  clear: { category: "phone_case", clear: true },
  // Opaque materials.
  tpu: { category: "phone_case", clear: false },
  silicone: { category: "color_case", clear: false },
  hard: { category: "phone_case", clear: false },
  leather: { category: "dark_case", clear: false },
  metal: { category: "dark_case", clear: false },
  wood: { category: "color_case", clear: false },
};

/** Map a base type to the editor's product profile (category + clear). Case-insensitive;
 *  unknown/undefined → an opaque phone-case default. */
export function baseTypeProfile(baseType?: string | null): { category: ProductCategory; clear: boolean } {
  const key = (baseType ?? "").trim().toLowerCase();
  return PROFILE_BY_TYPE[key] ?? { category: "phone_case", clear: false };
}

/** Human label for a base-type value; unknown values show as themselves (never blank). */
export function baseTypeLabel(baseType?: string | null): string {
  if (!baseType) return "";
  return BASE_TYPES.find((b) => b.value === baseType)?.label ?? baseType;
}
